import _ from "lodash";
import {
  KOHTEET,
  KOODISTOT,
  LUPA_SECTIONS,
  VAATIVA_TUKI_KOODIARVOT
} from "./constants";
import { parseLocalizedField } from "../modules/helpers";
import common from "../i18n/definitions/common";
import {
  assocPath,
  find,
  mapObjIndexed,
  path,
  prop,
  toUpper,
  filter,
  propEq
} from "ramda";

/**
 *
 * @param {object} lupa
 * @param {func} formatMessage
 * @param {string} locale
 * @param {object} muut
 */
export const parseLupa = (lupa, formatMessage, locale, muut) => {
  const localeUpper = toUpper(locale);
  if (lupa) {
    let lupaObj = {};
    let tyovoimaMaarays = checkTyovoima(lupa.maaraykset);

    mapObjIndexed((value, key) => {
      const { tunniste, headingNumber } = value;
      const currentMaaraykset = parseMaaraykset(lupa.maaraykset, tunniste);
      lupaObj[key] = parseSectionData(
        tunniste,
        currentMaaraykset,
        headingNumber,
        tyovoimaMaarays,
        formatMessage,
        localeUpper,
        muut
      );
    }, LUPA_SECTIONS);
    return lupaObj;
  }
};

const isVaativatuki = (koodiarvo, koodisto) => {
  return (
    koodisto === KOODISTOT.OIVA_MUUT &&
    VAATIVA_TUKI_KOODIARVOT.includes(koodiarvo)
  );
};

const parseSectionData = (
  target,
  maaraykset,
  headingNumber,
  tyovoimaMaarays,
  t,
  locale,
  muutKoodistot
) => {
  let returnobj = {};
  let tutkinnot = [];
  let muutMaaraykset = [];

  // kohde 1: Erikoiskäsittely tutkinnoille ja koulutuksille
  if (target === KOHTEET.TUTKINNOT) {
    const lisatietojaTutkinnot = [];
    const lisatietojaKoulutus = [];
    returnobj.heading = t(common.lupaSectionTutkinnotMainTitle);
    _.forEach(maaraykset, maarays => {
      const { koodisto, kohde, maaraystyyppi, uuid, meta } = maarays;

      switch (koodisto) {
        case KOODISTOT.KOULUTUS: {
          const { koodi, ylaKoodit, aliMaaraykset } = maarays;
          const { koodiArvo, metadata } = koodi;
          const tutkintoNimi = parseLocalizedField(metadata, locale);
          let tutkinto = {
            kohde,
            maaraystyyppi
          };

          // Käsitellään poikkeukset: valma (999901) ja telma (999903)
          if (koodiArvo === "999901") {
            muutMaaraykset.push({
              alkupvm: meta?.alkupvm,
              indeksi: muutMaaraykset.length + 1,
              kohde,
              koodi: koodiArvo,
              loppupvm: meta?.loppupvm,
              maaraysId: uuid,
              maaraystyyppi,
              nimi: tutkintoNimi,
              selite: t(common.lupaSectionTutkinnotValmaTitle)
            });
            return;
          }

          if (koodiArvo === "999903") {
            muutMaaraykset.push({
              alkupvm: meta?.alkupvm,
              indeksi: muutMaaraykset.length + 1,
              kohde,
              koodi: koodiArvo,
              loppupvm: meta?.loppupvm,
              maaraysId: uuid,
              maaraystyyppi,
              nimi: tutkintoNimi,
              selite: t(common.lupaSectionTutkinnotTelmaTitle)
            });
            return;
          }

          // Alimääräykset
          if (aliMaaraykset) {
            tutkinto.rajoitteet = [];
            _.forEach(aliMaaraykset, alimaarays => {
              const { koodi, kohde, koodisto } = alimaarays;
              if (koodi) {
                const { koodiArvo, metadata } = koodi;
                const nimi = parseLocalizedField(metadata, locale);
                tutkinto.rajoitteet.push({
                  koodi: koodiArvo,
                  nimi,
                  maaraysId: alimaarays.uuid,
                  maaraystyyppi: 2,
                  kohde,
                  koodisto: koodisto
                });
              }
            });
          }

          // Määritetään määräyksissä olevat alat yläkoodeista
          _.forEach(ylaKoodit, ylaKoodi => {
            const ylaKoodiKoodiArvo = ylaKoodi.koodiArvo;
            const ylaKoodiMetadata = ylaKoodi.metadata;
            const ylakoodiMetadataArvo = parseLocalizedField(
              ylaKoodiMetadata,
              locale
            );

            if (ylaKoodi.koodisto.koodistoUri === "isced2011koulutusalataso1") {
              tutkinto.koodi = koodiArvo;
              tutkinto.nimi = tutkintoNimi;
              tutkinto.alakoodi = ylaKoodiKoodiArvo;
              tutkinto.alanimi = ylakoodiMetadataArvo;
            } else if (ylaKoodi.koodisto.koodistoUri === "koulutustyyppi") {
              if (ylaKoodiKoodiArvo === 26) {
                tutkinto.koulutustyyppikoodi = 1;
                tutkinto.koulutustyyppi = "Ammatillinen perustutkinto";
              } else {
                tutkinto.koulutustyyppikoodi = ylaKoodiKoodiArvo;
                tutkinto.koulutustyyppi = ylakoodiMetadataArvo;
              }
            }
          });

          tutkinto.maaraysId = uuid;
          tutkinto.meta = meta;

          tutkinnot.push(tutkinto);
          break;
        }

        case KOODISTOT.AMMATILLISEEN_TEHTAVAAN_VALMISTAVA_KOULUTUS: {
          const { koodiarvo, koodisto, kohde, maaraystyyppi, meta } = maarays;

          const ammatillinenNimi = parseLocalizedField(
            maarays.koodi.metadata,
            locale,
            "nimi",
            "kieli"
          );

          muutMaaraykset.push({
            alkupvm: meta?.alkupvm,
            selite: t(common.lupaSectionTutkinnotAmmatillinenTitle),
            nimi: ammatillinenNimi,
            indeksi: muutMaaraykset.length + 1,
            maaraysId: uuid,
            koodiarvo,
            koodisto,
            kohde,
            loppupvm: meta?.loppupvm,
            maaraystyyppi
          });
          break;
        }

        case KOODISTOT.KULJETTAJAKOULUTUS: {
          const { koodiarvo, koodisto, kohde, maaraystyyppi, meta } = maarays;

          const kuljettajaSelite = maarays.koodi
            ? parseLocalizedField(
                maarays.koodi.metadata,
                locale,
                "kuvaus",
                "kieli"
              )
            : "";

          muutMaaraykset.push({
            alkupvm: meta?.alkupvm,
            selite: kuljettajaSelite,
            nimi: "",
            indeksi: muutMaaraykset.length + 1,
            maaraysId: uuid,
            koodiarvo,
            koodisto,
            kohde,
            loppupvm: meta?.loppupvm,
            maaraystyyppi
          });
          break;
        }

        case KOODISTOT.OIVA_TYOVOIMAKOULUTUS: {
          const { koodiarvo, koodisto, kohde, maaraystyyppi, meta } = maarays;

          const tyovoimaSelite = parseLocalizedField(
            maarays.koodi.metadata,
            locale,
            "kuvaus",
            "kieli"
          );

          muutMaaraykset.push({
            alkupvm: meta?.alkupvm,
            selite: tyovoimaSelite,
            nimi: "",
            indeksi: muutMaaraykset.length + 1,
            maaraysId: uuid,
            koodiarvo,
            koodisto,
            kohde,
            loppupvm: meta?.loppupvm,
            maaraystyyppi
          });
          break;
        }

        case KOODISTOT.OSAAMISALA: {
          break;
        }

        case KOODISTOT.LISATIETOJA: {
          if (maarays.kohde.tunniste === target) {
            const tunniste = maarays.meta?.tunniste;
            if (tunniste === "lisatiedot_tutkinnot") {
              lisatietojaTutkinnot.push(maarays);
            } else if (tunniste === "lisatiedot_koulutukset") {
              lisatietojaKoulutus.push(maarays);
            }
          }
          break;
        }

        default: {
          break;
        }
      }
    });

    muutMaaraykset = _.sortBy(muutMaaraykset, muuMaarays => {
      return muuMaarays.indeksi;
    });

    returnobj.maaraykset = sortTutkinnot(tutkinnot);
    returnobj.muutMaaraykset = muutMaaraykset;
    returnobj.lisatietojaTutkinnot = lisatietojaTutkinnot;
    returnobj.lisatietojaKoulutus = lisatietojaKoulutus;

    // kohde 2: Opetuskieli
  } else if (target === KOHTEET.KIELI) {
    returnobj.heading = t(common.lupaSectionOpetuskieliMainTitle);
    let opetuskielet = [];
    let tutkintokielet = [];
    const lisatietoja = [];
    let tutkintokieletArr = [];

    _.forEach(maaraykset, maarays => {
      const { koodisto, uuid } = maarays;
      const { aliMaaraykset } = maarays;

      // Alimääräykset
      if (aliMaaraykset) {
        _.forEach(aliMaaraykset, alimaarays => {
          const { kohde, maaraystyyppi } = alimaarays;
          if (alimaarays.koodiarvo) {
            const nimi = parseLocalizedField(maarays.koodi.metadata, locale);
            const tutkintokoodi = maarays.koodiarvo;
            const obj = {
              koodi: alimaarays.koodiarvo,
              maaraysId: uuid,
              nimi,
              tutkintokoodi,
              kohde,
              maaraystyyppi
            };

            tutkintokielet.push(obj);

            if (alimaarays.koodisto === KOODISTOT.KIELI) {
              const tutkinto = {
                tutkintokoodi: obj.tutkintokoodi,
                nimi: obj.nimi
              };
              let tutkintokieli = tutkintokieletArr.find(
                tutkintokieli =>
                  tutkintokieli.kieli === toUpper(alimaarays.koodiarvo)
              );
              if (tutkintokieli) {
                const exists = tutkintokieli.tutkinnot.some(
                  t => t.tutkintokoodi === tutkinto.tutkintokoodi
                );
                if (!exists) {
                  tutkintokieli.tutkinnot.push(tutkinto);
                }
              } else {
                tutkintokieletArr.push({
                  kieli: toUpper(alimaarays.koodiarvo),
                  tutkinnot: [tutkinto]
                });
              }
            }
            tutkintokieletArr.forEach(tutkintokieli => {
              tutkintokieli.tutkinnot.sort(
                (a, b) => a.tutkintokoodi - b.tutkintokoodi
              );
            });
          }
        });
      }

      if (koodisto === KOODISTOT.OPPILAITOKSENOPETUSKIELI) {
        opetuskielet.push(maarays);
      } else if (
        koodisto === KOODISTOT.LISATIETOJA &&
        maarays.kohde.tunniste === target
      ) {
        lisatietoja.push(maarays);
      }
    });

    // Create an object to hold the osaamisalarajoitteet for tutkinnot
    // {tutkinnon_koodiarvo: osaamisalarajoite_array ex. {1234: [...]}
    // Show osaamisala rajoite for tutkintokieli, similar to tutkinnot
    const tutkintoOsaamisalaObj = {};

    tutkintokieletArr.forEach(tutkintokieliObj => {
      const tutkinnot = tutkintokieliObj.tutkinnot;

      tutkinnot.forEach(tutkinto => {
        const tutkintokoodi = tutkinto.tutkintokoodi;

        // Find matching osaamisala objects in maaraykset
        const matchingOsaamisala = maaraykset.flatMap(maarays2 => {
          if (maarays2.koodiarvo === tutkintokoodi) {
            if (
              maarays2.aliMaaraykset &&
              Array.isArray(maarays2.aliMaaraykset)
            ) {
              return maarays2.aliMaaraykset.filter(aliMaar => {
                return aliMaar.koodisto === "osaamisala";
              });
            }
          }
          return [];
        });

        if (matchingOsaamisala.length > 0) {
          tutkintoOsaamisalaObj[tutkintokoodi] = matchingOsaamisala;
        }
      });
    });

    returnobj.kohdeKuvaus =
      opetuskielet.length > 1
        ? t(common.lupaSectionOpetuskieliPlural)
        : t(common.lupaSectionOpetuskieliSingular);
    returnobj.kohdeArvot = getMaaraysArvoArray(opetuskielet, locale);
    returnobj.tutkinnotjakielet = tutkintokielet;
    returnobj.lisatietoja = lisatietoja;
    returnobj.tutkintokieletArr = tutkintokieletArr;
    returnobj.tutkintoOsaamisalaObj = tutkintoOsaamisalaObj;

    // kohde 3: Toiminta-alueet
  } else if (target === KOHTEET.TOIMIALUE) {
    returnobj.heading = t(common.lupaSectionToimintaAlueMainTitle);

    let toimintaalueet = getToimintaalueArvoArray(maaraykset, locale);

    let maakunnat = _.filter(toimintaalueet, alue => {
      return alue.koodisto === "maakunta";
    });

    let kunnat = _.filter(toimintaalueet, alue => {
      return alue.koodisto === "kunta";
    });

    const lisatietoja = _.filter(
      maaraykset,
      maarays =>
        maarays.koodisto === KOODISTOT.LISATIETOJA &&
        maarays.kohde.tunniste === target
    );

    returnobj.maakunnat = maakunnat;
    returnobj.kunnat = kunnat;
    returnobj.lisatietoja = lisatietoja;

    if (maakunnat.length > 1 && kunnat.length > 1) {
      returnobj.kohdeKuvaus = t(
        common.lupaSectionToimintaAlueMaakuntaPluralKuntaPlural
      );
    }
    if (maakunnat.length === 1 && kunnat.length > 1) {
      returnobj.kohdeKuvaus = t(
        common.lupaSectionToimintaAlueMaakuntaSingularKuntaPlural
      );
    }
    if (maakunnat.length < 1 && kunnat.length > 1) {
      returnobj.kohdeKuvaus = t(
        common.lupaSectionToimintaAlueMaakuntaNoneKuntaPlural
      );
    }

    if (maakunnat.length > 1 && kunnat.length === 1) {
      returnobj.kohdeKuvaus = t(
        common.lupaSectionToimintaAlueMaakuntaPluralKuntaSingular
      );
    }
    if (maakunnat.length === 1 && kunnat.length === 1) {
      returnobj.kohdeKuvaus = t(
        common.lupaSectionToimintaAlueMaakuntaSingularKuntaSingular
      );
    }
    if (maakunnat.length < 1 && kunnat.length === 1) {
      returnobj.kohdeKuvaus = t(
        common.lupaSectionToimintaAlueMaakuntaNoneKuntaSingular
      );
    }

    if (maakunnat.length > 1 && kunnat.length < 1) {
      returnobj.kohdeKuvaus = t(
        common.lupaSectionToimintaAlueMaakuntaPluralKuntaNone
      );
    }
    if (maakunnat.length === 1 && kunnat.length < 1) {
      returnobj.kohdeKuvaus = t(
        common.lupaSectionToimintaAlueMaakuntaSingularKuntaNone
      );
    }
    if (maakunnat.length < 1 && kunnat.length < 1) {
      returnobj.kohdeKuvaus = t(common.lupaSectionToimintaAlueNone);
    }

    let valtakunnallinen = find(alue => {
      return (
        alue.koodisto === "nuts1" &&
        (alue.koodiarvo === "FI1" || alue.koodiarvo === "FI2")
      );
    }, toimintaalueet);

    if (valtakunnallinen) {
      returnobj.kohdeKuvaus = t(
        common.lupaSectionToimintaAlueNationalAlternative
      );
      returnobj.valtakunnallinen = valtakunnallinen;
    }

    // kohde 4: opiskelijavuodet
  } else if (target === KOHTEET.OPISKELIJAVUODET) {
    returnobj.heading = t(common.lupaSectionOpiskelijavuodetMainTitle);
    let opiskelijavuodet = [];
    let rajoitukset = [];
    const lisatietoja = [];

    _.forEach(maaraykset, maarays => {
      const { arvo, koodisto, kohde, maaraystyyppi, uuid, koodiarvo, koodi } =
        maarays;

      if (koodisto === KOODISTOT.OIVA_MUUT) {
        rajoitukset.push({
          arvo: arvo,
          maaraysUuid: uuid,
          koodiarvo: koodiarvo,
          koodi,
          kohde,
          maaraystyyppi,
          koodisto
        });
      } else if (
        koodisto === KOODISTOT.LISATIETOJA &&
        kohde.tunniste === target
      ) {
        lisatietoja.push(maarays);
      } else {
        opiskelijavuodet.push({
          arvo: arvo,
          maaraysUuid: uuid,
          koodiarvo: koodiarvo,
          kohde,
          maaraystyyppi,
          koodisto
        });
      }
    });

    if (rajoitukset.length === 1) {
      returnobj.kohdeKuvaus = t(
        common.lupaSectionOpiskelijavuodetLimitationSingular
      );
    }

    returnobj.opiskelijavuodet = opiskelijavuodet;
    returnobj.rajoitukset = rajoitukset;
    returnobj.lisatietoja = lisatietoja;

    // kohde 5: Muut
  } else if (target === KOHTEET.MUUT) {
    returnobj.heading = t(common.lupaSectionMuutMainTitle);
    let muut = [];
    let vaativat = [];
    let vankilat = [];
    let kokeilut = [];
    let yhteistyosopimukset = [];
    let toimintaedellytykset = [];
    let muutCombined = [];

    _.forEach(maaraykset, maarays => {
      const { koodi, meta, koodiarvo, koodisto, uuid, kohde, maaraystyyppi } =
        maarays;
      const { metadata } = koodi;

      if (koodi && metadata) {
        const type = parseLocalizedField(metadata, locale, "nimi", "kieli");
        const desc =
          parseLocalizedField(metadata, locale, "kuvaus", "kieli") ||
          "Ei kuvausta saatavilla";

        // Vaativa otsikko spesiaali tapausta varten
        const isTitle = propEq("koodiarvo", "24");
        const muutKoodistotFiltered = muutKoodistot
          ? filter(isTitle, muutKoodistot)
          : null;
        const vaativaTukiTitleByKoodiarvo = muutKoodistotFiltered
          ? path([0, "metadata", locale, "nimi"], muutKoodistotFiltered)
          : null;

        let obj = {
          tyyppi: isVaativatuki(koodiarvo, koodisto)
            ? vaativaTukiTitleByKoodiarvo
            : type,
          kuvaus: desc,
          koodiarvo: koodiarvo,
          koodisto: koodisto,
          maaraysId: uuid,
          kohde,
          maaraystyyppi
        };

        if (koodiarvo !== "22") {
          if (prop("kuvaus", meta)) {
            obj.value = prop("kuvaus", meta);
          }
        }

        switch (koodiarvo) {
          case "1": {
            muut.push(obj);
            break;
          }
          case "4": {
            muut.push(obj);
            break;
          }
          case "6": {
            muut.push(obj);
            break;
          }
          case "7": {
            // TODO localization
            const { kokeilu } = meta;
            const { fi } = kokeilu;
            obj.kuvaus = fi;
            kokeilut.push(obj);
            break;
          }
          case "8": {
            const { yhteistyosopimus } = meta;
            if (yhteistyosopimus) {
              obj.kuvaus = yhteistyosopimus.kuvaus;
              yhteistyosopimukset.push(obj);
            }
            break;
          }
          case "9": {
            toimintaedellytykset.push(obj);
            break;
          }
          case "10": {
            muut.push(obj);
            break;
          }
          case "11": {
            muut.push(obj);
            break;
          }
          case "2": {
            vaativat.push(obj);
            break;
          }
          case "3": {
            vaativat.push(obj);
            break;
          }
          case "12": {
            vaativat.push(obj);
            break;
          }
          case "5": {
            vankilat.push(obj);
            break;
          }
          case "13": {
            vankilat.push(obj);
            break;
          }
          /** Muu määräys: kajaanin kaupungin oma koodiarvo */
          case "15": {
            muut.push(obj);
            break;
          }
          case "19": {
            muut.push(obj);
            break;
          }
          /** Muu määräys */
          case "22": {
            obj = assocPath(
              ["meta", "createdAt"],
              maarays.meta?.createdAt,
              obj
            );
            if (prop("value", meta)) {
              obj.kuvaus = prop("value", meta);
              muut.push(obj);
            }
            break;
          }
          default:
            console.warn(`Unknown koodiarvo ${koodiarvo} in section 5. Muut`);
        }

        muutCombined.push(obj);
      }
    });

    returnobj.muut = muut;
    returnobj.vaativat = vaativat;
    returnobj.vankilat = vankilat;
    returnobj.kokeilut = kokeilut;
    returnobj.muutCombined = muutCombined;
    returnobj.toimintaedellytykset = toimintaedellytykset;
    returnobj.yhteistyosopimukset = yhteistyosopimukset;
  }

  returnobj.tunniste = target;
  returnobj.headingNumber = headingNumber;

  return returnobj;
};

function getMaaraysArvoArray(maaraykset, locale) {
  let arr = [];

  _.forEach(maaraykset, maarays => {
    const { koodi } = maarays;

    if (koodi) {
      const { metadata } = koodi;

      if (metadata) {
        // arr.push(parseLocalizedField(metadata))
        arr.push({
          ...maarays,
          value: maarays.koodiarvo,
          label: parseLocalizedField(metadata, locale)
        });
      }
    }
  });

  return arr;
}

function getToimintaalueArvoArray(maaraykset, locale) {
  let arr = [];

  _.forEach(maaraykset, maarays => {
    const { koodi, koodisto, koodiarvo, kohde, maaraystyyppi } = maarays;

    if (koodi) {
      const { metadata } = koodi;

      if (metadata) {
        arr.push({
          arvo: parseLocalizedField(metadata, locale),
          koodisto: koodisto,
          koodiarvo: koodiarvo,
          kohde,
          maaraystyyppi
        });
      }
    }
  });

  return arr;
}

function sortTutkinnot(tutkintoArray) {
  let obj = {};

  _.map(tutkintoArray, tutkinto => {
    const {
      alakoodi,
      alanimi,
      koodi,
      nimi,
      rajoitteet,
      koulutustyyppi,
      koulutustyyppikoodi,
      maaraystyyppi,
      kohde,
      maaraysId,
      meta
    } = tutkinto;
    const ala = obj[alakoodi];
    let koulutusalaObj = {};
    const tutkintoObj = {
      koodi,
      nimi,
      maaraysId,
      rajoitteet,
      maaraystyyppi,
      kohde,
      meta
    };

    if (ala === undefined) {
      // Alaa ei ole alat-objektissa
      // Tehdään koulutusala-objekti ja lisätään se juuritason objektiin
      koulutusalaObj[koulutustyyppikoodi] = {
        koodi: koulutustyyppikoodi,
        nimi: koulutustyyppi,
        koulutukset: [tutkintoObj]
      };
      obj[alakoodi] = {
        koodi: alakoodi,
        nimi: alanimi,
        koulutusalat: koulutusalaObj
      };
    } else {
      // Ala oli jo alat-objektissa
      // Tarkastetaan onko alalla koulutusalaa
      let koulAlaObj = ala.koulutusalat[koulutustyyppikoodi];

      if (koulAlaObj === undefined) {
        // koulutusalaa ei ollut koulutusaloissa, luodaan se
        ala.koulutusalat[koulutustyyppikoodi] = {
          koodi: koulutustyyppikoodi,
          nimi: koulutustyyppi,
          koulutukset: [tutkintoObj]
        };
      } else {
        // koulutusala löytyi koulutusaloista, lisätään tutkinto koulutusalan tutkintoihin
        koulAlaObj.koulutukset.push(tutkintoObj);
      }
    }
  });

  // Poistetaan mahdolliset tyhjät objektit
  obj = _.pickBy(obj, ala => {
    return ala.koodi !== undefined;
  });

  // Järjestetään objektit numerojärjestykseen
  obj = _.sortBy(obj, ala => {
    _.forEach(ala.koulutusalat, koulutusala => {
      koulutusala.koulutukset = _.sortBy(koulutusala.koulutukset, tutkinto => {
        return tutkinto.koodi;
      });
    });
    return ala.koodi;
  });

  return obj;
}

function parseMaaraykset(maaraykset, kohdeTunniste) {
  if (!maaraykset) {
    return null;
  }

  if (kohdeTunniste !== KOHTEET.KIELI) {
    return _.filter(maaraykset, maarays => {
      return path(["kohde", "tunniste"], maarays) === kohdeTunniste;
    });
  } else {
    // oppilaitoksen opetuskieli
    let opetuskielet = _.filter(maaraykset, maarays => {
      return path(["kohde", "tunniste"], maarays) === KOHTEET.KIELI;
    });

    // tutkintokielet
    let tutkintokielet = _.filter(maaraykset, maarays => {
      return path(["kohde", "tunniste"], maarays) === KOHTEET.TUTKINNOT;
    });

    return _.concat(opetuskielet, tutkintokielet);
  }
}

function checkTyovoima(maaraykset) {
  if (!maaraykset) {
    return null;
  }

  // mikäli järjestäjällä on vain työvoimakoulutusta, palautetaan määräys opiskelijavuositekstiä varten
  return _.filter(maaraykset, maarays => {
    if (maarays.koodisto === KOODISTOT.OIVA_TYOVOIMAKOULUTUS)
      return maarays.koodiarvo === "3";
  });
}
